import {colorToHexString} from '@Utils/color.util';

/** Default text color applied when no explicit color is found in the selection. */
export const DEFAULT_TEXT_COLOR = '#242424';

/** Snapshot of the selection fields compared across RTE updates. */
export type SelectionSnapshot = Partial<Pick<Selection, 'anchorNode' | 'focusNode' | 'anchorOffset' | 'focusOffset' | 'isCollapsed'>>;

/** Captures the relevant fields of a Selection for change-detection without retaining a live reference. */
export function copySelectionSnapshot(selection: Selection | null): SelectionSnapshot | null {
  if (!selection) {
    return null;
  }

  return {
    anchorNode: selection.anchorNode,
    focusNode: selection.focusNode,
    anchorOffset: selection.anchorOffset,
    focusOffset: selection.focusOffset,
    isCollapsed: selection.isCollapsed,
  };
}

export function hasSelectionChanged(last: SelectionSnapshot | null, current: Selection): boolean {
  if (!last) {
    return false;
  }

  return last.anchorNode !== current.anchorNode || last.focusNode !== current.focusNode || last.anchorOffset !== current.anchorOffset || last.focusOffset !== current.focusOffset;
}

/** Returns true when the anchor and focus nodes both sit inside an `<a>` element. */
export function isCurrentSelectionLink(selection: Selection): boolean {
  if (selection.rangeCount === 0) {
    return false;
  }

  const node = selection.anchorNode;

  if (node?.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === 'A') {
    return true;
  }

  return node?.nodeType === Node.TEXT_NODE && node.parentElement?.tagName === 'A';
}

/**
 * Returns the computed hex color of the text at the current selection, falling back to
 * {@link DEFAULT_TEXT_COLOR} when no meaningful selection exists.
 */
export function getColorFillForCurrentText(selection: Selection | null): string {
  if (!selection || selection.rangeCount === 0) {
    return DEFAULT_TEXT_COLOR;
  }

  const range = selection.getRangeAt(0);
  const selectedNode = range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE ? (range.commonAncestorContainer as HTMLElement) : range.commonAncestorContainer.parentElement;

  if (!selectedNode) {
    return DEFAULT_TEXT_COLOR;
  }

  return colorToHexString(window.getComputedStyle(selectedNode).color);
}

/** Returns the href on the focused node (or its parent) of the current selection, or an empty string. */
export function getLinkOptionURL(selection: Selection | null, fallbackEl: HTMLElement | null | undefined): string {
  const selectedNode = (selection?.focusNode ?? fallbackEl) as HTMLElement | null;

  if (!selectedNode) {
    return '';
  }

  if (typeof selectedNode.getAttribute === 'function') {
    return selectedNode.getAttribute('href') ?? '';
  }

  return selectedNode.parentElement?.getAttribute('href') ?? '';
}

export function getBooleanFromString(value: string): boolean {
  return value.toLowerCase() === 'true';
}
